import type { QueryClient } from "@tanstack/react-query";
import { EventBus } from "../../contexts";
import { AuthContextValue, AuthUser } from "../../contexts/auth/types/auth";
import { IDateValue, logger, LOGS } from "../../utils";
import { setQueryDataOverFetch } from "../../utils/query/setQueryDataOverFetch";
import { ApiManager } from "../api";
import { isNonRetryableApiError } from "../api/errors";
import { LicenseState } from "../license/LicenseState";
import { USER_PROFILE_KEY } from "./queries";
import { UserProfile } from "./types/user";

export class UserProfileLoader {
    private userId: string;

    private eventBus: EventBus;

    private authContext: AuthContextValue;

    private apiManager: ApiManager;

    private licenseState: LicenseState;

    private lastDbLoginDate: IDateValue = "";

    private isUserJustCreated: boolean = false;

    /** Nombre de tentatives bootstrap effectuées depuis le dernier _cleanup().
     *  Incrémenté dans _initialize() — utilisé par _bootstrapUserProfile() pour
     *  distinguer une erreur transitoire (retry possible) d'un échec définitif. */
    private _bootstrapAttemptCount: number = 0;
    private readonly _BOOTSTRAP_MAX_ATTEMPTS: number = 2; // withRetry retente une fois

    private fetchingUserProfile: Promise<any> | null = null;

    private queryClient: QueryClient;

    constructor(
        authUser: AuthUser,
        eventBus: EventBus,
        authContext: AuthContextValue,
        apiManager: ApiManager,
        licenseState: LicenseState,
        queryClient: QueryClient,
    ) {
        this.userId = authUser.id ?? "unknown";
        this.eventBus = eventBus;
        this.authContext = authContext;
        this.apiManager = apiManager;
        this.licenseState = licenseState;
        this.queryClient = queryClient;
    }

    public get cachedProfile(): UserProfile | null {
        return (
            this.queryClient.getQueryData<UserProfile>(USER_PROFILE_KEY) ?? null
        );
    }

    public updateCachedProfile(
        update: (profile: UserProfile) => UserProfile,
    ): void {
        setQueryDataOverFetch<UserProfile>(
            this.queryClient,
            USER_PROFILE_KEY,
            (profile) => (profile ? update(profile) : profile),
        );
    }

    public get maxBootstrapAttempts(): number {
        return this._BOOTSTRAP_MAX_ATTEMPTS;
    }

    public startBootstrapAttempt(): number {
        return ++this._bootstrapAttemptCount;
    }

    public _isUserJustCreated(): boolean {
        return this.isUserJustCreated;
    }

    public getLastLoginDate(): IDateValue {
        return this.lastDbLoginDate;
    }

    public _cleanup(): void {
        if (this.fetchingUserProfile) {
            logger.info(
                LOGS.UserManager,
                "    Cancelling pending user profile fetch",
            );
            // Note: Can't actually cancel Promise, but we'll reset the reference
            this.fetchingUserProfile = null;
        }

        this.lastDbLoginDate = "";
        this.userId = "unknown";
        this._bootstrapAttemptCount = 0;
    }

    /**
     * @method getUserProfile
     * Récupère le profil utilisateur à jour en Gèrant les appels concurrents
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {boolean} forceFetchNewProfile - Force la récupération depuis Graph API si true
     * @output {Promise<any>} Profil utilisateur
     */
    public async getUserProfile(
        forceFetchNewProfile: boolean = false,
    ): Promise<UserProfile> {
        const rand = Math.floor(Math.random() * 1000);
        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            `[${rand}] - getUserProfile(${forceFetchNewProfile})`,
        );

        // Si une requête est déjà en cours, attendez son résultat
        if (this.fetchingUserProfile) {
            logger.info(
                [LOGS.UserProfile, LOGS.UserManager],
                `   [${rand}] - => Waiting for ongoing profile fetch ...`,
            );
            return this.fetchingUserProfile;
        }

        logger.info(LOGS.UserProfile, `   [${rand}] - => Fetching profile`);
        this.fetchingUserProfile = this.fetchUserProfile(
            forceFetchNewProfile,
            rand,
        );

        try {
            const profile = await this.fetchingUserProfile;
            logger.info(
                LOGS.UserProfile,
                `   [${rand}] - => Profile fetched`,
                profile,
            );

            logger.info(
                LOGS.UserProfile,
                `   [${rand}] - => Returning profile for ${profile?.displayName}`,
            );
            return profile;
        } catch (error) {
            logger.error(
                LOGS.UserProfile,
                `   [${rand}] - => Error fetching user profile ! ${error}`,
            );
            throw new Error(
                `   [${rand}] - Error fetching user profile : ` + error,
            );
        } finally {
            logger.info(
                LOGS.UserProfile,
                `   [${rand}] - Reseting this.fetchingUserProfile`,
            );
            this.fetchingUserProfile = null;
        }
    }

    /**
     * @method fetchUserProfile
     * Retourne le profil du storage, sinon génère un nouveau profile complet et le retourne
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {boolean} forceFetchNewProfile - Force la reconstruction d'un profile si true
     * @output {Promise<any>} Profil utilisateur
     */
    private async fetchUserProfile(
        forceFetchNewProfile: boolean = false,
        rand: number,
    ): Promise<UserProfile> {
        logger.info(
            LOGS.UserProfile,
            `[${rand}] - UserManager.fetchUserProfile()`,
        );

        // On vérifie d'abord le cache local
        if (!forceFetchNewProfile) {
            logger.info(LOGS.UserProfile, `[${rand}] - Checking local cache`);

            const storedData = this.cachedProfile;
            logger.info(
                LOGS.UserProfile,
                `[${rand}] - Storage userProfile`,
                storedData,
            );

            if (storedData) {
                return storedData;
            }
        }

        // Si pas de cache valide, on recalcule le profil utilisateur
        logger.info(
            LOGS.UserProfile,
            `  [${rand}] - Building up to date profile ...`,
        );

        await this._bootstrapUserProfile();

        logger.info(
            LOGS.UserProfile,
            `  [${rand}] - Checking local cache again`,
        );

        const newStoredData = this.cachedProfile;

        if (newStoredData) {
            logger.info(
                LOGS.UserProfile,
                `[${rand}] - Storage userProfile`,
                newStoredData,
            );
            return newStoredData;
        }

        logger.error(
            LOGS.UserProfile,
            `[${rand}] - No user profile found in cache after syncUserProfile()`,
        );
        throw new Error(
            `[${rand}] - No user profile found in cache after syncUserProfile()`,
        );
    }

    /**
     * @method _bootstrapUserProfile
     *
     * Initialise le profil utilisateur via POST v1/me/bootstrap.
     * Remplace syncUserProfile() : la résolution/création DB, la trial license et le lastLoginDate
     * sont désormais gérés côté backend. Le SDK récupère ensuite les licences (Azure Function),
     * le contexte scoring et la photo de profil.
     * ═══════════════════════════════════════════════════════════════════════════
     * @output {Promise<void>}
     */
    private async _bootstrapUserProfile(): Promise<void> {
        const attempt = this._bootstrapAttemptCount;
        const maxAttempts = this._BOOTSTRAP_MAX_ATTEMPTS;
        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            `_bootstrapUserProfile() — tentative ${attempt}/${maxAttempts} — user : ${this.userId}`,
        );

        // 1. Appel bootstrap — le backend orchestre : résolution/création profil DB,
        //    customViews, trial license, lastLoginDate, et récupération des licences.
        let bootstrapData;
        try {
            bootstrapData = await this.apiManager.bootstrapUser(attempt);
        } catch (error) {
            if (attempt < maxAttempts) {
                logger.warn(
                    [LOGS.UserProfile, LOGS.UserManager],
                    `_bootstrapUserProfile — tentative ${attempt}/${maxAttempts} échouée (erreur transitoire — retry en cours)`,
                );
            } else {
                logger.error(
                    [LOGS.UserProfile, LOGS.UserManager],
                    `_bootstrapUserProfile — tentative ${attempt}/${maxAttempts} échouée — bootstrap définitivement échoué`,
                );
            }
            logger.info([LOGS.UserProfile, LOGS.UserManager], "error", error);

            if (isNonRetryableApiError(error)) {
                throw error;
            }

            const apiErrorMessage =
                error instanceof Error ? error.message : String(error);
            throw new Error(`Error calling bootstrap API : ${apiErrorMessage}`);
        }

        const userProfile: UserProfile = { ...bootstrapData.userProfile };

        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            "  _bootstrapUserProfile - userProfile",
            userProfile,
        );

        // 1ter. Refresh forcé du token Keycloak si le backend a attribué un rôle realm
        //       pendant le bootstrap (typiquement ORGANIZATION_ADMIN sur un nouvel admin B2B).
        //       Doit aboutir AVANT la suite du flow : await direct de sorte que le JWT en mémoire
        //       reflète les nouveaux rôles avant la navigation vers le dashboard et les premiers
        //       appels API protégés par roles.
        if (bootstrapData.tokenRefreshRequired) {
            logger.info(
                [LOGS.UserProfile, LOGS.UserManager],
                `  _bootstrapUserProfile - tokenRefreshRequired=true → demande de refresh du token`,
            );
            await this.authContext.forceRefresh();
        }

        // 2. Détection nouvel utilisateur
        this.isUserJustCreated = bootstrapData.isNewUser;
        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            `  _bootstrapUserProfile - isNewUser: ${this.isUserJustCreated}`,
        );

        // 3. Événement compatibilité LicenseManager (utilisateurs existants uniquement)
        if (!bootstrapData.isNewUser) {
            this.eventBus.emit("startup-user-db-profile-fetched", userProfile);
        }

        // 4. lastDbLoginDate (avant la mise à jour par le backend)
        this.lastDbLoginDate = userProfile.profile.lastLoginDate || "";
        logger.info(
            LOGS.UserManager,
            `User last DB login date : ${this.lastDbLoginDate}`,
        );

        // 5. Licences — alimenter le cache LicenseManager depuis les données bootstrap (zéro appel réseau supplémentaire)
        this.licenseState.seedFromBootstrap(bootstrapData.licenses);
        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            "  _bootstrapUserProfile - licenseManager seeded from bootstrap",
        );

        // 6. Cache local
        //    Note : getUserContext() et getUserPhotoByEmail() sont différés
        //    dans _initializePostConsent() (Phase 2, après consentement Didomi).
        this.queryClient.setQueryData(USER_PROFILE_KEY, userProfile);
        this.eventBus.emit("ia-startup-token-status");

        // 9. Événements
        if (this.isUserJustCreated) {
            this.eventBus.emit("user-created-profile-updated");
        }
    }
}
